import React, { useState, createContext, useContext } from 'react';
export type Sex = 'male' | 'female' | 'unspecified';
export type CustomerTag = 'vip' | 'regular' | 'new' | 'dormant' | 'birthday-month';
export type CustomerOrder = {
  id: string;
  date: string; // ISO
  total: number;
  items: {
    name: string;
    qty: number;
    unitPrice: number;
  }[];
  method: 'dineIn' | 'takeout' | 'delivery' | 'orderAhead';
  branchId: string;
  status: 'completed' | 'cancelled' | 'refunded';
  rating?: number; // 1-5 if customer rated
};
export type Customer = {
  id: string;
  firstName: string;
  lastName: string;
  sex: Sex;
  phone: string;
  email?: string;
  birthDate?: string; // ISO
  joined: string; // ISO
  tags: CustomerTag[];
  notes: string;
  marketingOptIn: boolean;
  preferredBranchId?: string;
  orders: CustomerOrder[];
};
type Ctx = {
  customers: Customer[];
  updateCustomer: (id: string, patch: Partial<Customer>) => void;
  toggleTag: (id: string, tag: CustomerTag) => void;
  captureLead: (phone: string, patch?: Partial<Customer>) => void;
};
const CRMContext = createContext<Ctx | null>(null);
const uid = () => Math.random().toString(36).slice(2, 9);
// Helper to make a date relative to today
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};
const makeOrder = (daysBack: number, total: number, method: CustomerOrder['method'], items: CustomerOrder['items'], branchId = 'b_main', status: CustomerOrder['status'] = 'completed', rating?: number): CustomerOrder => ({
  id: `ord_${uid()}`,
  date: daysAgo(daysBack),
  total,
  items,
  method,
  branchId,
  status,
  rating
});
const initialCustomers: Customer[] = [{
  id: 'c1',
  firstName: 'Unknown',
  lastName: 'Unknown',
  sex: 'male',
  phone: '251981878468',
  birthDate: '2026-05-10',
  joined: '2026-05-11T15:25:53',
  tags: ['new'],
  notes: '',
  marketingOptIn: false,
  preferredBranchId: 'b_main',
  orders: [makeOrder(2, 480, 'dineIn', [{
    name: 'Beef Burger Deluxe',
    qty: 2,
    unitPrice: 240
  }])]
}, {
  id: 'c2',
  firstName: 'Nati',
  lastName: 'Hotto',
  sex: 'male',
  phone: '251937599434',
  email: 'nati.hotto@example.com',
  birthDate: '1999-12-31',
  joined: '2026-05-11T15:27:52',
  tags: ['regular'],
  notes: 'Always orders extra hot sauce. Prefers window seating.',
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(1, 720, 'dineIn', [{
    name: 'Beef Burger Deluxe',
    qty: 2,
    unitPrice: 240
  }, {
    name: 'French fries',
    qty: 1,
    unitPrice: 80
  }, {
    name: 'Iced Latte',
    qty: 1,
    unitPrice: 90
  }], 'b_main', 'completed', 5), makeOrder(7, 460, 'orderAhead', [{
    name: 'American Breakfast',
    qty: 1,
    unitPrice: 320
  }, {
    name: 'Mango Smoothie',
    qty: 1,
    unitPrice: 140
  }], 'b_main', 'completed', 5), makeOrder(14, 280, 'takeout', [{
    name: 'Caesar Salad',
    qty: 1,
    unitPrice: 280
  }], 'b_main'), makeOrder(28, 540, 'delivery', [{
    name: 'Pepperoni Pizza',
    qty: 1,
    unitPrice: 480
  }, {
    name: 'Garlic bread',
    qty: 1,
    unitPrice: 60
  }], 'b_main', 'completed', 4)]
}, {
  id: 'c3',
  firstName: 'Meiraf',
  lastName: 'Tegegne',
  sex: 'female',
  phone: '251910170129',
  email: 'meiraf.t@example.com',
  birthDate: '1996-01-30',
  joined: '2026-05-14T19:10:44',
  tags: ['vip', 'regular'],
  notes: 'VIP — celebrates anniversary every January 30. Allergic to peanuts.',
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(1, 1240, 'dineIn', [{
    name: 'Truffle Risotto',
    qty: 1,
    unitPrice: 620
  }, {
    name: 'House Wine',
    qty: 2,
    unitPrice: 310
  }], 'b_main', 'completed', 5), makeOrder(5, 980, 'dineIn', [{
    name: 'Grilled Salmon',
    qty: 1,
    unitPrice: 580
  }, {
    name: 'Caesar Salad',
    qty: 1,
    unitPrice: 280
  }, {
    name: 'Tiramisu',
    qty: 1,
    unitPrice: 120
  }], 'b_main', 'completed', 5), makeOrder(12, 1450, 'dineIn', [{
    name: 'Tomahawk Steak',
    qty: 1,
    unitPrice: 1200
  }, {
    name: 'Roasted vegetables',
    qty: 1,
    unitPrice: 180
  }, {
    name: 'Sparkling water',
    qty: 1,
    unitPrice: 70
  }], 'b_main', 'completed', 5), makeOrder(21, 760, 'orderAhead', [{
    name: 'American Breakfast',
    qty: 2,
    unitPrice: 320
  }], 'b_main'), makeOrder(35, 890, 'delivery', [{
    name: 'Margherita Pizza',
    qty: 1,
    unitPrice: 420
  }, {
    name: 'Tiramisu',
    qty: 1,
    unitPrice: 120
  }, {
    name: 'House Red Wine',
    qty: 1,
    unitPrice: 310
  }], 'b_main', 'completed', 4), makeOrder(45, 540, 'takeout', [{
    name: 'Sushi Platter',
    qty: 1,
    unitPrice: 540
  }], 'b_main')]
}, {
  id: 'c4',
  firstName: 'Natnael',
  lastName: 'Chane',
  sex: 'male',
  phone: '251912964433',
  email: 'natnael.c@example.com',
  birthDate: '1991-01-18',
  joined: '2026-05-15T14:04:56',
  tags: ['regular'],
  notes: 'Usually orders lunch around 1pm. Loves spicy food.',
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(2, 420, 'takeout', [{
    name: 'Spicy Doro Wat',
    qty: 1,
    unitPrice: 380
  }, {
    name: 'Coca-Cola',
    qty: 1,
    unitPrice: 40
  }], 'b_main'), makeOrder(9, 380, 'takeout', [{
    name: 'Kitfo',
    qty: 1,
    unitPrice: 380
  }], 'b_main'), makeOrder(16, 460, 'dineIn', [{
    name: 'Tibs',
    qty: 1,
    unitPrice: 420
  }, {
    name: 'Ambo Water',
    qty: 1,
    unitPrice: 40
  }], 'b_main', 'completed', 5)]
}, {
  id: 'c5',
  firstName: 'Robel',
  lastName: 'Bahru',
  sex: 'male',
  phone: '251940452472',
  birthDate: '1989-03-09',
  joined: '2026-05-15T14:24:36',
  tags: ['vip'],
  notes: '',
  marketingOptIn: false,
  preferredBranchId: 'b_main',
  orders: [makeOrder(3, 1620, 'delivery', [{
    name: 'Family Pizza Combo',
    qty: 1,
    unitPrice: 1200
  }, {
    name: 'Garden Salad x2',
    qty: 2,
    unitPrice: 210
  }], 'b_main', 'completed', 5), makeOrder(10, 980, 'dineIn', [{
    name: 'Mixed Grill',
    qty: 1,
    unitPrice: 780
  }, {
    name: 'Espresso',
    qty: 2,
    unitPrice: 100
  }], 'b_main', 'completed', 5), makeOrder(18, 1100, 'dineIn', [{
    name: 'Tomahawk Steak',
    qty: 1,
    unitPrice: 1200
  }], 'b_main', 'completed', 4)]
}, {
  id: 'c6',
  firstName: 'Feysel',
  lastName: 'Hussen',
  sex: 'male',
  phone: '251913032397',
  birthDate: '1999-12-31',
  joined: '2026-05-15T14:45:05',
  tags: ['dormant'],
  notes: "Hasn't ordered in over 60 days — consider a re-engagement promo.",
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(72, 320, 'takeout', [{
    name: 'Shawarma',
    qty: 1,
    unitPrice: 280
  }, {
    name: 'Bottled Water',
    qty: 1,
    unitPrice: 40
  }], 'b_main')]
}, {
  id: 'c7',
  firstName: 'Mohammad',
  lastName: 'Awol',
  sex: 'male',
  phone: '251914648693',
  birthDate: '1999-12-31',
  joined: '2026-05-15T15:00:35',
  tags: ['regular'],
  notes: '',
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(4, 540, 'orderAhead', [{
    name: 'Cheeseburger',
    qty: 2,
    unitPrice: 240
  }, {
    name: 'Sweet potato fries',
    qty: 1,
    unitPrice: 60
  }], 'b_main'), makeOrder(11, 460, 'dineIn', [{
    name: 'Chicken Sandwich',
    qty: 1,
    unitPrice: 380
  }, {
    name: 'Iced Tea',
    qty: 1,
    unitPrice: 80
  }], 'b_main', 'completed', 4), makeOrder(22, 280, 'takeout', [{
    name: 'Garden Salad',
    qty: 1,
    unitPrice: 240
  }, {
    name: 'Sparkling water',
    qty: 1,
    unitPrice: 40
  }], 'b_main')]
}, {
  id: 'c8',
  firstName: 'Abebaw',
  lastName: 'Zegeye',
  sex: 'male',
  phone: '251913551050',
  email: 'abebaw.z@example.com',
  birthDate: '1996-02-02',
  joined: '2026-05-16T18:12:32',
  tags: ['new'],
  notes: 'First-time customer, redeemed welcome offer.',
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(0, 320, 'dineIn', [{
    name: 'Pepperoni Pizza',
    qty: 1,
    unitPrice: 320
  }], 'b_main', 'completed', 5)]
}, {
  id: 'c9',
  firstName: 'Biniyam',
  lastName: 'Eyasu',
  sex: 'male',
  phone: '251912266184',
  birthDate: '1991-12-22',
  joined: '2026-05-18T08:30:46',
  tags: ['regular'],
  notes: '',
  marketingOptIn: false,
  preferredBranchId: 'b_main',
  orders: [makeOrder(2, 380, 'delivery', [{
    name: 'Doro Wat',
    qty: 1,
    unitPrice: 380
  }], 'b_main', 'completed', 4), makeOrder(8, 540, 'dineIn', [{
    name: 'Mixed Grill',
    qty: 1,
    unitPrice: 460
  }, {
    name: 'Mango Juice',
    qty: 1,
    unitPrice: 80
  }], 'b_main', 'completed', 5), makeOrder(17, 220, 'takeout', [{
    name: 'Sambusa Trio',
    qty: 1,
    unitPrice: 180
  }, {
    name: 'Tea',
    qty: 1,
    unitPrice: 40
  }], 'b_main')]
}, {
  id: 'c10',
  firstName: 'Netsanet',
  lastName: 'Eshetu',
  sex: 'female',
  phone: '251903708494',
  birthDate: '1999-12-31',
  joined: '2026-05-18T09:16:20',
  tags: ['new', 'birthday-month'],
  notes: '',
  marketingOptIn: true,
  preferredBranchId: 'b_main',
  orders: [makeOrder(1, 280, 'dineIn', [{
    name: 'Cappuccino',
    qty: 2,
    unitPrice: 80
  }, {
    name: 'Croissant',
    qty: 1,
    unitPrice: 120
  }], 'b_main', 'completed', 5)]
}];
export function CRMProvider({
  children
}: {
  children: ReactNode;
}) {
  const [customers, setCustomers] = useState<Customer[]>(initialCustomers);
  const updateCustomer = (id: string, patch: Partial<Customer>) => {
    setCustomers(prev => prev.map(c => c.id === id ? {
      ...c,
      ...patch
    } : c));
  };
  const toggleTag = (id: string, tag: CustomerTag) => {
    setCustomers(prev => prev.map(c => {
      if (c.id !== id) return c;
      return {
        ...c,
        tags: c.tags.includes(tag) ? c.tags.filter(t => t !== tag) : [...c.tags, tag]
      };
    }));
  };
  const captureLead = (phone: string, patch?: Partial<Customer>) => {
    setCustomers(prev => {
      const existing = prev.find(c => c.phone === phone);
      if (existing) {
        return prev.map(c => c.phone === phone ? {
          ...c,
          ...patch
        } : c);
      }
      // Create new partial lead
      const newCustomer: Customer = {
        id: `c_${uid()}`,
        firstName: 'Unknown',
        lastName: 'Unknown',
        sex: 'unspecified',
        phone,
        joined: new Date().toISOString(),
        tags: ['new'],
        notes: '',
        marketingOptIn: false,
        orders: [],
        ...patch
      };
      return [...prev, newCustomer];
    });
  };
  return <CRMContext.Provider value={{
    customers,
    updateCustomer,
    toggleTag,
    captureLead
  }} data-id="element-1364">
      {children}
    </CRMContext.Provider>;
}
export function useCRM() {
  const ctx = useContext(CRMContext);
  if (!ctx) throw new Error('useCRM must be used within CRMProvider');
  return ctx;
}
// Derived customer metrics
export type CustomerMetrics = {
  totalOrders: number;
  lifetimeSpend: number;
  avgOrderValue: number;
  lastOrderDate: string | null;
  daysSinceLastOrder: number | null;
  favoriteMethod: CustomerOrder['method'] | null;
  topItems: {
    name: string;
    count: number;
  }[];
  rating: number | null;
};
export function getCustomerMetrics(customer: Customer): CustomerMetrics {
  const completed = customer.orders.filter(o => o.status === 'completed');
  const totalOrders = completed.length;
  const lifetimeSpend = completed.reduce((s, o) => s + o.total, 0);
  const avgOrderValue = totalOrders > 0 ? lifetimeSpend / totalOrders : 0;
  const sorted = [...customer.orders].sort((a, b) => b.date.localeCompare(a.date));
  const lastOrderDate = sorted[0]?.date ?? null;
  const daysSinceLastOrder = lastOrderDate ? Math.floor((Date.now() - new Date(lastOrderDate).getTime()) / (1000 * 60 * 60 * 24)) : null;
  // Favorite method (mode)
  const methodCounts: Record<string, number> = {};
  completed.forEach(o => {
    methodCounts[o.method] = (methodCounts[o.method] || 0) + 1;
  });
  const favoriteMethod = Object.entries(methodCounts).sort((a, b) => b[1] - a[1])[0]?.[0] as CustomerOrder['method'] ?? null;
  // Top items by total quantity
  const itemMap: Record<string, number> = {};
  completed.forEach(o => o.items.forEach(i => {
    itemMap[i.name] = (itemMap[i.name] || 0) + i.qty;
  }));
  const topItems = Object.entries(itemMap).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([name, count]) => ({
    name,
    count
  }));
  // Average rating
  const rated = completed.filter(o => o.rating !== undefined);
  const rating = rated.length > 0 ? rated.reduce((s, o) => s + (o.rating || 0), 0) / rated.length : null;
  return {
    totalOrders,
    lifetimeSpend,
    avgOrderValue,
    lastOrderDate,
    daysSinceLastOrder,
    favoriteMethod,
    topItems,
    rating
  };
}
export const tagMeta: Record<CustomerTag, {
  label: string;
  color: string;
  icon: string;
}> = {
  vip: {
    label: 'VIP',
    color: 'amber',
    icon: 'crown'
  },
  regular: {
    label: 'Regular',
    color: 'emerald',
    icon: 'repeat'
  },
  new: {
    label: 'New',
    color: 'blue',
    icon: 'sparkles'
  },
  dormant: {
    label: 'Dormant',
    color: 'gray',
    icon: 'moon'
  },
  'birthday-month': {
    label: 'Birthday',
    color: 'pink',
    icon: 'cake'
  }
};
export const methodLabels: Record<CustomerOrder['method'], string> = {
  dineIn: 'Dine-in',
  takeout: 'Takeout',
  delivery: 'Delivery',
  orderAhead: 'Order ahead'
};